import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteConfirmationComponent, DeleteConfirmationData } from './delete-confirmation.component';
import { AuthService } from '../../auth.service';
import { AufnahmePunktDocument } from '../../data-model';
import { ConfirmService } from '@luzula/luzula-lib';

/**
 * Service für erweiterte Lösch-Bestätigungen mit zusätzlichen Sicherheitsprüfungen:
 * - Bei > 5 Punkten: Anzahl eintippen
 * - Warnung bei kontrollierten Punkten
 * - Hinweis bei fremden Punkten (anderer Kartierer)
 */
@Injectable({
	providedIn: 'root'
})
export class DeleteConfirmationService {
	private modalService = inject(NgbModal);
	private aus = inject(AuthService);
	private confirmService = inject(ConfirmService);

	/**
	 * Zeigt einen erweiterten Lösch-Dialog für einen einzelnen Punkt.
	 * Bei einfachen Fällen (nicht kontrolliert, eigener Punkt) wird der Standard-Dialog verwendet.
	 */
	async confirmDeleteSingle(point: AufnahmePunktDocument): Promise<boolean> {
		const isControlled = point.state?.resolved === true;
		const isForeign = this.isForeignPoint(point);

		// Einfacher Fall: Standard-Dialog
		if (!isControlled && !isForeign) {
			return this.confirmService.confirmDelete(
				`Soll der Punkt '${point.header?.nummer || point._id}' wirklich gelöscht werden?`
			);
		}

		// Erweiterter Dialog
		return this.showDeleteConfirmation({
			count: 1,
			controlledCount: isControlled ? 1 : 0,
			foreignCount: isForeign ? 1 : 0,
			currentUser: this.getCurrentUserName()
		});
	}

	/**
	 * Schnelle Variante für Bulk-Delete ohne Laden der vollständigen Daten.
	 * Verwendet die bereits im Speicher vorhandenen reduzierten Punkt-Daten.
	 */
	async confirmDeleteBulkFast(points: { _id: string; state?: { resolved?: boolean }; header?: { aufnahme?: { kartierer?: string } } }[]): Promise<boolean> {
		const count = points.length;

		if (count === 0) return false;

		let controlledCount = 0;
		let foreignCount = 0;

		for (const point of points) {
			if (point.state?.resolved === true) {
				controlledCount++;
			}
			if (this.isForeignPointReduced(point)) {
				foreignCount++;
			}
		}

		// Einfacher Fall
		if (count <= 5 && controlledCount === 0 && foreignCount === 0) {
			return this.confirmService.confirmDelete(
				`Sollen wirklich ${count} Punkte gelöscht werden?`
			);
		}

		// Erweiterter Dialog
		return this.showDeleteConfirmation({
			count,
			controlledCount,
			foreignCount,
			currentUser: this.getCurrentUserName()
		});
	}

	private async showDeleteConfirmation(data: DeleteConfirmationData): Promise<boolean> {
		const modalRef = this.modalService.open(DeleteConfirmationComponent, {
			centered: true,
			backdrop: 'static'
		});

		modalRef.componentInstance.data = data;

		try {
			await modalRef.result;
			return true;
		} catch {
			return false;
		}
	}

	private isForeignPoint(point: AufnahmePunktDocument): boolean {
		const kartierer = point.header?.aufnahme?.kartierer;
		if (!kartierer) return false;

		const currentUser = this.getCurrentUserName();
		if (!currentUser) return false;

		// Vergleiche Kartierer-Namen (case-insensitive)
		return kartierer.toLowerCase() !== currentUser.toLowerCase();
	}

	private isForeignPointReduced(point: { header?: { aufnahme?: { kartierer?: string } } }): boolean {
		const kartierer = point.header?.aufnahme?.kartierer;
		if (!kartierer) return false;

		const currentUser = this.getCurrentUserName();
		if (!currentUser) return false;

		return kartierer.toLowerCase() !== currentUser.toLowerCase();
	}

	private getCurrentUserName(): string {
		// currentUser ist der volle Name (user.name aus Keycloak)
		return this.aus.currentUser || this.aus.currentUserId || '';
	}
}
